/**
 * Sidebar v2 row shared parts: the row prop contract, the hover snooze
 * menu, and the forge worker fan-out.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (SnoozeMenuButton ~439-500, row props ~957-1017) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: the worker fan-out reuses forge's
 * WorkerSubRow and expansion store; snooze presets resolve through this
 * port's sidebarV2Snooze module.
 */
import { ClockIcon } from "lucide-react";
import { useMemo } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { ScopedThreadRef, TimestampFormat } from "@cmmd-forge/contracts";

import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "~/components/ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { WorkerSubRow } from "~/components/sidebar/WorkerSubRow";
import { CSS } from "@dnd-kit/utilities";
import { useSidebarWorkerExpansion } from "~/components/sidebar/useSidebarWorkerExpansion";
import type { SidebarThreadSummary } from "~/types";
import type { ProjectCanonicalIdentity } from "~/components/ProjectIcon";
import type { ThreadOrigin } from "~/components/sidebar/threadOrigin";
import type { SidebarV2Thread } from "./sidebarV2Threads";

type WorkerId = SidebarThreadSummary["id"];
type WorkerEnvironmentId = SidebarThreadSummary["environmentId"];
import type { SortableThreadRowBag } from "./sidebarV2Chrome";
import { resolveSnoozePresets, type SnoozePreset } from "./sidebarV2Snooze";

/** Keys for the snooze presets riding the row's hover menu. */
export type SidebarSnoozeChoice = Pick<SnoozePreset, "snoozedUntil">;

export interface SidebarV2RowProps {
  readonly thread: SidebarV2Thread;
  readonly variant: "card" | "slim";
  /** Slim rows wake (snoozed) or un-settle (settled); cards settle. */
  readonly variantAction: "settle" | "unsettle" | "unsnooze";
  readonly isPinned: boolean;
  readonly sortable?: SortableThreadRowBag | undefined;
  readonly dropVerb: string | null;
  readonly dragOverPinned: boolean;
  readonly snoozeWakeLabelText: string | null;
  readonly wokeAt: string | null;
  readonly isActive: boolean;
  readonly jumpLabel: string | null;
  readonly environmentLabel: string | null;
  readonly environmentMachine: string | null;
  readonly projectCwd: string | null;
  readonly projectCanonicalIdentity?: ProjectCanonicalIdentity | undefined;
  readonly projectDisplayName: string | null;
  readonly threadOrigin?: ThreadOrigin;
  readonly linkedPrUrl: string | null;
  readonly timestampFormat: TimestampFormat;
  /** Master-thread workers (forge fan-out). Empty elsewhere. */
  readonly workerRows: readonly {
    readonly id: WorkerId;
    readonly environmentId: WorkerEnvironmentId;
    readonly title: string;
    readonly status: string;
    readonly workerRole: string | null;
  }[];
  readonly onThreadClick: (event: ReactMouseEvent, threadRef: ScopedThreadRef) => void;
  readonly onThreadActivate: (threadRef: ScopedThreadRef) => void;
  readonly onStartRename: (threadRef: ScopedThreadRef, title: string) => void;
  readonly onRenameTitleChange: (title: string) => void;
  readonly onCommitRename: (
    threadRef: ScopedThreadRef,
    title: string,
    originalTitle: string,
  ) => void;
  readonly onCancelRename: () => void;
  readonly isRenaming: boolean;
  readonly renamingTitle: string;
  readonly onContextMenu: (threadRef: ScopedThreadRef, position: { x: number; y: number }) => void;
  readonly onSettle: (threadRef: ScopedThreadRef) => void;
  readonly onUnsettle: (threadRef: ScopedThreadRef) => void;
  readonly onSnooze: (threadRef: ScopedThreadRef, preset: SidebarSnoozeChoice) => void;
  readonly onUnsnooze: (threadRef: ScopedThreadRef) => void;
  readonly onUnpin: (threadRef: ScopedThreadRef) => void;
  readonly onAcknowledgeWoke: (threadRef: ScopedThreadRef, visitedAt: string) => void;
  readonly onOpenPullRequest: (event: ReactMouseEvent<HTMLElement>, url: string) => void;
  readonly onNavigateWorker: (worker: {
    readonly environmentId: WorkerEnvironmentId;
    readonly id: WorkerId;
  }) => void;
}

export const DROP_VERB_LABEL: Record<string, string> = {
  pin: "Pin",
  unpin: "Unpin",
  settle: "Settle",
  unsettle: "Unsettle",
  wake: "Wake",
};

/** The dnd-kit root props for a sortable row: ref, transform, transition,
 * visibility sentinel, and the pointer listeners. */
export function useSortableRootProps(sortable: SortableThreadRowBag | undefined) {
  if (!sortable) return {};
  return {
    ref: sortable.setNodeRef,
    style: {
      transform: CSS.Translate.toString(sortable.transform),
      transition: sortable.transition,
      visibility:
        !sortable.isDragging && sortable.transform?.scaleY === 0 ? ("hidden" as const) : undefined,
    },
    ...sortable.listeners,
  };
}

/** Hover entry point for snooze: a clock button opening the preset menu.
 * Presets resolve at open time so "In 1 hour" is relative to the click. */
export function SnoozeMenuButton(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSnooze: (preset: SidebarSnoozeChoice) => void;
  timestampFormat: TimestampFormat;
}) {
  const presets = useMemo(
    () => (props.open ? resolveSnoozePresets(new Date(), props.timestampFormat) : []),
    [props.open, props.timestampFormat],
  );
  return (
    <Menu open={props.open} onOpenChange={props.onOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={
            <MenuTrigger
              render={
                <button
                  type="button"
                  aria-label="Snooze thread"
                  onClick={(event) => event.stopPropagation()}
                  onDoubleClick={(event) => event.stopPropagation()}
                  className="inline-flex h-full cursor-pointer items-center gap-0.5 rounded-md bg-transparent px-1.5 text-xs text-muted-foreground hover:text-foreground"
                />
              }
            />
          }
        >
          <ClockIcon className="size-3" />
        </TooltipTrigger>
        <TooltipPopup>Snooze thread</TooltipPopup>
      </Tooltip>
      <MenuPopup side="bottom" align="end">
        {presets.map((preset) => (
          <MenuItem
            key={preset.id}
            onClick={(event) => {
              event.stopPropagation();
              props.onSnooze(preset);
            }}
          >
            {preset.label}
            <span className="ml-auto text-xs text-muted-foreground">{preset.whenLabel}</span>
          </MenuItem>
        ))}
        <MenuSeparator />
        <MenuItem
          onClick={async (event) => {
            event.stopPropagation();
            const { requestCustomSnooze } = await import("./sidebarV2CustomSnoozeDialog");
            const choice = await requestCustomSnooze();
            if (choice) props.onSnooze(choice);
          }}
        >
          Custom…
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

/** Forge worker fan-out under a master thread's card, reusing the legacy
 * row's expansion store and sub-rows. */
export function WorkerFanOut(props: {
  readonly threadKey: string;
  readonly workerRows: readonly {
    readonly id: WorkerId;
    readonly environmentId: WorkerEnvironmentId;
    readonly title: string;
    readonly status: string;
    readonly workerRole: string | null;
  }[];
  readonly onNavigateWorker: (worker: {
    readonly environmentId: WorkerEnvironmentId;
    readonly id: WorkerId;
  }) => void;
}) {
  const expansion = useSidebarWorkerExpansion();
  const expanded = expansion.expandedWorkersByThread.has(props.threadKey);
  if (props.workerRows.length === 0) return null;
  return (
    <div className="flex min-w-0 flex-col" data-testid={`sidebar-workers-${props.threadKey}`}>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={(event) => {
          event.stopPropagation();
          expansion.toggleWorkersForThread(props.threadKey);
        }}
        className="inline-flex w-fit cursor-pointer items-center gap-1 rounded-sm px-2 pb-0.5 text-2xs text-muted-foreground hover:text-foreground"
      >
        {expanded ? "Hide" : "Show"} workers ({props.workerRows.length})
      </button>
      {expanded
        ? props.workerRows.map((worker) => (
            <WorkerSubRow
              key={worker.id}
              id={worker.id}
              title={worker.title}
              status={worker.status as never}
              workerRole={worker.workerRole as never}
              onNavigate={() => props.onNavigateWorker(worker)}
            />
          ))
        : null}
    </div>
  );
}
